import { Stack, Tab, Tabs, Typography } from '@mui/material'
import { type FC } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { recruiterRouteConfig } from 'recruiter/routes'

interface AdminHeaderProps {
    isAdmin: boolean
    isBotAdmin: boolean
}

// Shared header for the /admin surface. Renders the page title and a
// sub-nav that picks between the Recruiters and Bot Audit pages.
// Both pages under /admin share this header so the Admin group is the
// only thing the top nav advertises - Bot Audit is a sub-tab, not a
// peer of Admin. Takes role flags as props so parents (which already
// fetch RecruiterMe for their own gating) don't duplicate the query.
export const AdminHeader: FC<AdminHeaderProps> = ({ isAdmin, isBotAdmin }) => {
    const { pathname } = useLocation()

    const onBotAudit = pathname.startsWith(recruiterRouteConfig.botAudit.path)
    const onBucketAudit = pathname.startsWith(
        recruiterRouteConfig.bucketAudit.path
    )
    const activeValue = onBucketAudit
        ? 'bucket-audit'
        : onBotAudit || !isAdmin
          ? 'bot-audit'
          : 'recruiters'

    const tabs: Array<{ value: string; label: string; to: string }> = []
    if (isAdmin) {
        tabs.push({
            value: 'recruiters',
            label: 'Recruiters',
            to: recruiterRouteConfig.admin.path,
        })
    }
    if (isAdmin) {
        tabs.push({
            value: 'bucket-audit',
            label: 'Bucket Audit',
            to: recruiterRouteConfig.bucketAudit.path,
        })
    }
    if (isBotAdmin) {
        tabs.push({
            value: 'bot-audit',
            label: 'Bot Audit',
            to: recruiterRouteConfig.botAudit.path,
        })
    }
    // For users with both roles, "Admin" is the umbrella group - they see
    // sub-tabs to pick between Recruiters and Bot Audit. A bot-admin-only
    // user redirected to /admin/bot-audit shouldn't see "Admin" with no
    // tabs - that reads as a dead end on a bot-specific page.
    const title = isBotAdmin && !isAdmin ? 'Bot Audit' : 'Admin'

    return (
        <Stack spacing={1}>
            <Typography variant="h4" data-testid="admin-header-title">
                {title}
            </Typography>
            {tabs.length > 1 ? (
                <Tabs
                    value={activeValue}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                    data-testid="admin-subnav"
                >
                    {tabs.map((t) => (
                        <Tab
                            key={t.value}
                            value={t.value}
                            label={t.label}
                            component={Link}
                            to={t.to}
                            sx={{ textTransform: 'none' }}
                            data-testid={`admin-subnav-${t.value}`}
                        />
                    ))}
                </Tabs>
            ) : null}
        </Stack>
    )
}
